import { useId, useState } from 'react';

/**
 * Piezas de grafico, en SVG y CSS propios.
 *
 * Decisiones que vienen del metodo y no del gusto:
 *
 *  - Todas las barras van del MISMO color. Son categorias nominales (paises,
 *    dispositivos) y la longitud ya codifica el valor; pintar cada una de un
 *    color gastaria el canal de identidad en repetir lo que la barra ya dice, y
 *    ademas sugeriria que los colores significan algo.
 *  - El color se valido con el comprobador de la paleta contra las dos
 *    superficies reales del panel, clara y oscura. No se eligio a ojo.
 *  - El texto nunca lleva el color del dato: un azul claro es ilegible como
 *    texto. La identidad la aporta la marca, no la letra.
 *  - Las rejillas son de un paso sobre el fondo y de 1px solido. Tienen que
 *    poder ignorarse.
 */

/** Cifra grande con su etiqueta. Para lo que se lee de un vistazo. */
export function StatTile({
  value,
  label,
  hint,
}: {
  readonly value: number;
  readonly label: string;
  readonly hint?: string;
}) {
  return (
    <div className="tile">
      <span className="tile__value">{value.toLocaleString('es')}</span>
      <span className="tile__label">{label}</span>
      {hint ? <span className="tile__hint">{hint}</span> : null}
    </div>
  );
}

export interface PuntoTemporal {
  readonly dia: string;
  readonly valor: number;
}

/**
 * Serie temporal como area de una sola serie.
 *
 * Sin leyenda: con una sola serie, el titulo ya dice que es. Se etiqueta solo
 * el maximo, porque un numero sobre cada punto no lo lee nadie.
 */
export function AreaTemporal({
  puntos,
  etiqueta,
}: {
  readonly puntos: readonly PuntoTemporal[];
  readonly etiqueta: string;
}) {
  const id = useId();
  const [activo, setActivo] = useState<number | null>(null);

  if (puntos.length === 0) {
    return <p className="muted small">Todavía no hay datos que mostrar.</p>;
  }

  const ancho = 720;
  const alto = 180;
  const margen = { arriba: 16, abajo: 24, izq: 8, der: 8 };
  const util = {
    ancho: ancho - margen.izq - margen.der,
    alto: alto - margen.arriba - margen.abajo,
  };

  const max = Math.max(1, ...puntos.map((p) => p.valor));
  const paso = puntos.length > 1 ? util.ancho / (puntos.length - 1) : 0;

  const x = (i: number) => margen.izq + i * paso;
  const y = (v: number) => margen.arriba + util.alto - (v / max) * util.alto;

  const linea = puntos.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.valor)}`).join(' ');
  const area = `${linea} L${x(puntos.length - 1)},${margen.arriba + util.alto} L${x(0)},${margen.arriba + util.alto} Z`;

  const indiceMax = puntos.reduce(
    (mejor, p, i) => (p.valor > (puntos[mejor]?.valor ?? 0) ? i : mejor),
    0,
  );

  const punto = activo === null ? null : puntos[activo];

  return (
    <div className="chart">
      <svg
        viewBox={`0 0 ${ancho} ${alto}`}
        className="chart__svg"
        role="img"
        aria-label={`${etiqueta}: ${puntos.reduce((s, p) => s + p.valor, 0)} en ${puntos.length} dias`}
        onMouseLeave={() => setActivo(null)}
      >
        {/* Rejilla recesiva: tres lineas bastan para dar escala. */}
        {[0, 0.5, 1].map((f) => (
          <line
            key={f}
            x1={margen.izq}
            x2={ancho - margen.der}
            y1={margen.arriba + util.alto * f}
            y2={margen.arriba + util.alto * f}
            className="chart__grid"
          />
        ))}

        <path d={area} className="chart__area" />
        <path d={linea} className="chart__line" />

        {/* El maximo lleva marcador y etiqueta; los demas no. */}
        {puntos.length > 1 ? (
          <>
            <circle
              cx={x(indiceMax)}
              cy={y(puntos[indiceMax]?.valor ?? 0)}
              r={4.5}
              className="chart__dot"
            />
            <text
              x={Math.min(x(indiceMax), ancho - 40)}
              y={Math.max(y(puntos[indiceMax]?.valor ?? 0) - 10, 12)}
              className="chart__peak"
              textAnchor={x(indiceMax) > ancho - 80 ? 'end' : 'middle'}
            >
              {puntos[indiceMax]?.valor.toLocaleString('es')}
            </text>
          </>
        ) : null}

        {/* Zonas de contacto: mas anchas que la marca, para que el raton
            encuentre el punto sin precision de cirujano. */}
        {puntos.map((p, i) => (
          <rect
            key={p.dia}
            x={x(i) - paso / 2}
            y={margen.arriba}
            width={Math.max(paso, 8)}
            height={util.alto}
            fill="transparent"
            onMouseEnter={() => setActivo(i)}
          />
        ))}

        {activo !== null ? (
          <line
            x1={x(activo)}
            x2={x(activo)}
            y1={margen.arriba}
            y2={margen.arriba + util.alto}
            className="chart__crosshair"
          />
        ) : null}

        <desc id={id}>{etiqueta}</desc>
      </svg>

      <div className="chart__axis">
        <span>{formatoDia(puntos[0]?.dia)}</span>
        <span>{formatoDia(puntos[puntos.length - 1]?.dia)}</span>
      </div>

      {punto ? (
        <p className="chart__tooltip" role="status">
          <strong>{punto.valor.toLocaleString('es')}</strong> el {formatoDia(punto.dia, true)}
        </p>
      ) : (
        <p className="chart__tooltip chart__tooltip--vacio" aria-hidden="true">
          Pasa el ratón por la gráfica
        </p>
      )}
    </div>
  );
}

export interface Reparto {
  readonly clave: string;
  readonly etiqueta: string;
  readonly valor: number;
}

/**
 * Barras horizontales ordenadas de mayor a menor.
 *
 * Horizontales porque los nombres son largos (paises, navegadores) y en
 * vertical habria que girarlos, que es el camino corto a lo ilegible.
 */
export function BarrasReparto({
  datos,
  vacio = 'Sin datos todavia.',
  maximo = 6,
}: {
  readonly datos: readonly Reparto[];
  readonly vacio?: string;
  readonly maximo?: number;
}) {
  if (datos.length === 0) return <p className="muted small">{vacio}</p>;

  const ordenado = [...datos].sort((a, b) => b.valor - a.valor);
  const visibles = ordenado.slice(0, maximo);
  const resto = ordenado.slice(maximo);

  // Mas alla de un punado de categorias, los nombres dejan de leerse y la
  // cola no aporta: se agrupa y el detalle queda en la exportacion.
  const filas =
    resto.length > 0
      ? [
          ...visibles,
          {
            clave: '__otros',
            etiqueta: `Otros (${resto.length})`,
            valor: resto.reduce((s, d) => s + d.valor, 0),
          },
        ]
      : visibles;

  const total = ordenado.reduce((s, d) => s + d.valor, 0) || 1;
  const max = Math.max(...filas.map((f) => f.valor), 1);

  return (
    <ul className="bars">
      {filas.map((fila) => {
        const porcentaje = Math.round((fila.valor / total) * 100);
        return (
          <li key={fila.clave} className="bars__row">
            <span className="bars__label" title={fila.etiqueta}>
              {fila.etiqueta}
            </span>
            <span className="bars__track">
              <span
                className="bars__fill"
                style={{ inlineSize: `${Math.max((fila.valor / max) * 100, 1.5)}%` }}
              />
            </span>
            <span className="bars__value">
              {fila.valor.toLocaleString('es')}
              <small> {porcentaje}%</small>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Las 24 horas del dia como columnas.
 *
 * Es ordinal: el orden significa algo, asi que van siempre las 24 aunque
 * alguna este a cero. Saltarse las horas vacias deformaria el patron, que es
 * justo lo que se viene a ver.
 */
export function ColumnasHora({ porHora }: { readonly porHora: Readonly<Record<string, number>> }) {
  const [activa, setActiva] = useState<number | null>(null);
  const horas = Array.from({ length: 24 }, (_, h) => porHora[String(h)] ?? 0);
  const max = Math.max(1, ...horas);
  const total = horas.reduce((s, v) => s + v, 0);

  if (total === 0) return <p className="muted small">Sin datos todavía.</p>;

  const puntaIndice = horas.indexOf(max);

  return (
    <div className="hours">
      <div className="hours__plot" onMouseLeave={() => setActiva(null)}>
        {horas.map((valor, h) => (
          <button
            key={h}
            type="button"
            className={`hours__col${activa === h ? ' hours__col--activa' : ''}`}
            onMouseEnter={() => setActiva(h)}
            onFocus={() => setActiva(h)}
            aria-label={`${h}:00 — ${valor} escaneos`}
          >
            <span
              className="hours__fill"
              style={{ blockSize: `${Math.max((valor / max) * 100, valor > 0 ? 4 : 0)}%` }}
            />
          </button>
        ))}
      </div>

      <div className="hours__axis">
        <span>00h</span>
        <span>06h</span>
        <span>12h</span>
        <span>18h</span>
        <span>23h</span>
      </div>

      <p className="chart__tooltip" role="status">
        {activa === null ? (
          <>
            Hora punta: <strong>{puntaIndice}:00</strong> con{' '}
            {max.toLocaleString('es')} escaneos. Horario UTC.
          </>
        ) : (
          <>
            <strong>{horas[activa]?.toLocaleString('es')}</strong> escaneos a las{' '}
            {activa}:00 UTC
          </>
        )}
      </p>
    </div>
  );
}

function formatoDia(dia: string | undefined, largo = false): string {
  if (!dia) return '';
  const fecha = new Date(`${dia}T00:00:00Z`);
  if (Number.isNaN(fecha.getTime())) return dia;
  return fecha.toLocaleDateString('es', {
    day: 'numeric',
    month: largo ? 'long' : 'short',
    timeZone: 'UTC',
  });
}
